import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import {
  API,
  BookingBaseInfoDTO,
  DeviceFullDTO,
  MediumFullDTO,
  MediumTransformationDTO,
  OwnedScreenDTO,
  ScreenHardwareDTO,
  ScreenPlaylistDTO,
  StaticPlaylistSlotDTO,
} from '../../../../services/api/api';
import { ActivatedRoute } from '@angular/router';
import { UtilService } from '../../../../services/util.service';
import { ErrorService } from '../../../../services/error.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { finalize, take } from 'rxjs/operators';
import { PlaylistSchemaComponent } from '../../../../components/sb-playlist-view/playlist-schema-iteration/playlist-schema.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { MediumTransformationListComponent } from '../../medium-transformation-list/medium-transformation-list.component';
import { ScreenHardwareComponent } from '../../../datasets/screen-hardware/screen-hardware.component';
import { PermissionService } from '../../../../services/permission.service';
import { ToasterService, ToastType } from '../../../../services/toaster/toaster.service';

export interface ScreenDTOEnhanced extends OwnedScreenDTO {
  playlistVisible?: boolean;
  renderedTemplates?: boolean;
}

@Component({
  selector: 'sb-page-device-details-screens',
  templateUrl: './device-details-screens.component.html',
  styleUrls: ['./device-details-screens.component.scss'],
  standalone: false,
})
export class DeviceDetailsScreensComponent implements OnInit {
  toasterService = inject(ToasterService);
  deviceId: number;
  device: DeviceFullDTO;
  screens: ScreenDTOEnhanced[];

  screenPlaylist: ScreenPlaylistDTO;
  loading: boolean;
  playlistLoading: boolean;

  constructor(
    private readonly api: API,
    private readonly activatedRoute: ActivatedRoute,
    public readonly util: UtilService,
    public readonly modalService: ModalService,
    public readonly errorHandler: ErrorService,
    private readonly destroyRef: DestroyRef,
    private readonly translate: TranslateService,
    public readonly permissionService: PermissionService
  ) {
    this.loading = true;
    this.playlistLoading = true;
    this.screenPlaylist = null;
  }

  ngOnInit() {
    this.activatedRoute.parent.params.subscribe(params => {
      this.deviceId = +params.deviceID;
      this.refresh();
    });
  }

  refresh() {
    this.loading = true;
    this.api
      .ss_nm_Device_retrieveFullById(this.deviceId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (e: DeviceFullDTO) => {
          this.device = e;
          this.screens = [];
          this.device.screens.every(screen => this.screens.push({ ...screen, playlistVisible: false, renderedTemplates: false }));
        },
        error: error => {
          if (!(error.status && (+error.status === 403 || +error.status === 404))) {
            this.errorHandler.errorHandler(error);
          }
        },
      });
  }

  openScreenPlaylistScheme(playlist: ScreenPlaylistDTO) {
    this.modalService
      .showNavigationModal({
        entryComponent: { value: PlaylistSchemaComponent, data: { playlistScheme: playlist?.playlistSchema } },
        title: 'Playlist: ' + playlist?.playlistSchema?.schedule?.name,
        modalOptions: { size: 'xl' },
      })
      .then(() => {})
      .catch(() => {});
  }

  refreshPlaylist(id: number) {
    if (id == null) {
      return;
    }
    this.playlistLoading = true;
    this.screenPlaylist = null;
    this.api
      .ss_nm_Screen_retrieveLiveScreenPlaylist(id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.playlistLoading = false;
        })
      )
      .subscribe({
        next: (playlist: ScreenPlaylistDTO) => {
          this.screenPlaylist = playlist;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  getMediumForSlot(playlist: ScreenPlaylistDTO, slot: StaticPlaylistSlotDTO): MediumFullDTO {
    if (!slot || !playlist) {
      return null;
    }
    let medium: MediumFullDTO = null;
    for (const booking of playlist.renderedBookings) {
      if (booking.id === slot.bookingId) {
        medium = booking.medium;
        return medium;
      }
    }
    return medium;
  }

  getBookingForSlot(playlist: ScreenPlaylistDTO, slot: StaticPlaylistSlotDTO): BookingBaseInfoDTO {
    if (!slot || !playlist) {
      return null;
    }
    return playlist.renderedBookings.find(booking => booking.id === slot.bookingId);
  }

  changePlaylistVisible(screen: ScreenDTOEnhanced) {
    if (!screen) {
      return;
    }
    screen.playlistVisible = !screen.playlistVisible;
    if (screen.playlistVisible) {
      this.refreshPlaylist(screen?.id);
    }
  }

  showRenderedTemplateEntries(screen: ScreenDTOEnhanced) {
    if (!screen) {
      return;
    }
    screen.renderedTemplates = !screen.renderedTemplates;
  }

  removeMediumTransformation(screen: ScreenDTOEnhanced) {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.globals.removeMediumTransformation'),
        message: this.translate.instant('lang.globals.removeMediumTransformationMessage'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          screen.mediumTransformation = null;
          this.updateDeviceWithMediumTransformation(screen);
        }
      });
  }

  updateDeviceWithMediumTransformation(editedScreen: ScreenDTOEnhanced) {
    for (const screen of this.device.screens) {
      if (screen.id === editedScreen.id) {
        screen.mediumTransformation = editedScreen.mediumTransformation;
        break;
      }
    }
    this.api.ss_nm_Device_editDevice(this.device).subscribe({
      next: () => {
        this.refresh();
      },
      error: error => {
        this.errorHandler.errorHandler(error);
      },
    });
  }

  addMediumTransformation(screen: ScreenDTOEnhanced) {
    this.modalService
      .showNavigationModal({
        entryComponent: { value: MediumTransformationListComponent, data: {} },
        title: null,
        modalOptions: {
          // windowClass: 'bigXL',
          size: 'xl',
        },
      })
      .then((transformation: MediumTransformationDTO) => {
        if (transformation) {
          this.modalService
            .confirm({
              title: this.translate.instant('lang.globals.addMediumTransformation'),
              message: this.translate.instant('lang.globals.addMediumTransformationMessage'),
              yes: this.translate.instant('lang.globals.yes'),
              no: this.translate.instant('lang.globals.no'),
            })
            .then((result: boolean) => {
              if (result) {
                screen.mediumTransformation = transformation;
                this.updateDeviceWithMediumTransformation(screen);
              }
            });
        }
      })
      .catch(() => {});
  }

  removeScreenHardware(screen: ScreenDTOEnhanced) {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.device.screenHardware.delete'),
        message: this.translate.instant('lang.device.screenHardware.deleteMessage'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          screen.hardware = null;
          this.updateDeviceWithScreenHardware(screen);
        }
      });
  }

  updateDeviceWithScreenHardware(editedScreen: ScreenDTOEnhanced) {
    for (const screen of this.device.screens) {
      if (screen.id === editedScreen.id) {
        screen.hardware = editedScreen.hardware;
        break;
      }
    }
    this.api.ss_nm_Device_editDevice(this.device).subscribe({
      next: () => {
        this.refresh();
      },
      error: error => {
        this.errorHandler.errorHandler(error);
      },
    });
  }

  addScreenHardware(screen: ScreenDTOEnhanced) {
    this.modalService
      .showNavigationModal({
        entryComponent: { value: ScreenHardwareComponent, data: {} },
        title: null,
        modalOptions: {
          // windowClass: 'bigXL',
          size: 'xl',
        },
      })
      .then((hardware: ScreenHardwareDTO) => {
        if (hardware) {
          this.modalService
            .confirm({
              title: this.translate.instant('lang.device.screenHardware.add'),
              message: this.translate.instant('lang.device.screenHardware.addMessage'),
              yes: this.translate.instant('lang.globals.yes'),
              no: this.translate.instant('lang.globals.no'),
            })
            .then((result: boolean) => {
              if (result) {
                screen.hardware = hardware;
                this.updateDeviceWithScreenHardware(screen);
              }
            });
        }
      })
      .catch(() => {});
  }

  deleteScreenPlaylistCache(screen: ScreenDTOEnhanced) {
    if (!screen.id) {
      throw new Error('Missing Screen ID');
    }
    this.modalService
      .confirm({
        title: this.translate.instant('lang.globals.deleteScreenPlaylistCache') + '?',
        message: this.translate.instant('lang.globals.deleteScreenPlaylistCacheMessage'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api.ss_nm_Screen_deleteScreenPlaylistCache(screen.id).subscribe({
            next: () => {
              this.toasterService.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.deleteSuccess'));
            },
          });
        }
      })
      .catch(() => {});
  }

  deleteDevicePlaylistCache() {
    if (!this.deviceId) {
      throw new Error('Missing Device ID');
    }
    this.modalService
      .confirm({
        title: this.translate.instant('lang.globals.deleteDevicePlaylistCache') + '?',
        message: this.translate.instant('lang.globals.deleteDevicePlaylistCacheMessage'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api.ss_nm_Device_deleteDevicePlaylistCache(this.deviceId).subscribe({
            next: () => {
              this.toasterService.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.deleteSuccess'));
            },
          });
        }
      })
      .catch(() => {});
  }
}
